import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './DeliveryClaimsPage.css';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || 'https://api.beetlestore.online').replace(/\/$/, '');
const API_KEY     = process.env.REACT_APP_BACKEND_API_KEY || '';
const HEADERS     = { 'Content-Type': 'application/json', 'X-API-Key': API_KEY };

async function apiFetch(path, opts = {}) {
  const res  = await fetch(`${BACKEND_URL}${path}`, { headers: HEADERS, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const DeliveryClaimsPage = () => {
  const { t } = useTranslation();
  const REASON_META = {
    driver_silent: { label: t('deliveryClaims.reasonDriverSilent'), color: '#ef4444', bg: '#fef2f2' },
    not_confirmed: { label: t('deliveryClaims.reasonNotConfirmed'), color: '#f59e0b', bg: '#fffbeb' },
    no_driver:     { label: t('deliveryClaims.reasonNoDriver'),     color: '#7c3aed', bg: '#f5f3ff' },
  };

  const reasonMeta = (reason) =>
    REASON_META[reason] || { label: reason || t('deliveryClaims.unknown'), color: '#6b7280', bg: '#f3f4f6' };

  const [data, setData]       = useState({ releases: [], total: 0, by_reason: {}, by_driver: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await apiFetch('/api/admin/delivery-claims');
      setData(res);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const topDrivers = Object.entries(data.by_driver || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  return (
    <div className="dc-page">
      <div className="dc-header">
        <div>
          <h1 className="dashboard-title">{t('nav.deliveryClaims')}</h1>
          <p className="dashboard-subtitle">{t('deliveryClaims.subtitle')}</p>
        </div>
        <button className="dc-refresh" onClick={load} disabled={loading}>
          {loading ? '…' : `↻ ${t('common.refresh')}`}
        </button>
      </div>

      {error && (
        <div className="dc-error">{error} <button onClick={load}>{t('common.retry')}</button></div>
      )}

      {!error && (
        <div className="dc-row2">
          <div className="dc-summary-card">
            <div className="dc-summary-icon" style={{ background: '#f3f4f6' }}>↩︎</div>
            <div>
              <div className="dc-summary-label">{t('deliveryClaims.recentReleases')}</div>
              <div className="dc-summary-val">{data.total ?? 0}</div>
            </div>
          </div>
          {Object.entries(data.by_reason || {}).map(([reason, count]) => {
            const rm = reasonMeta(reason);
            return (
              <div className="dc-summary-card" key={reason}>
                <div className="dc-summary-icon" style={{ background: rm.bg, color: rm.color }}>●</div>
                <div>
                  <div className="dc-summary-label">{rm.label}</div>
                  <div className="dc-summary-val">{count}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && !error && topDrivers.length > 0 && (
        <div className="dc-section">
          <h2 className="dc-section-title">{t('deliveryClaims.topDriversTitle')}</h2>
          <div className="dc-driver-list">
            {topDrivers.map(([driverId, count]) => (
              <div className="dc-driver-row" key={driverId}>
                <code className="dc-code">{driverId}</code>
                <span className="dc-driver-count">{t('deliveryClaims.releaseCount', { count })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && !error && (
        <div className="dc-section">
          <h2 className="dc-section-title">{t('deliveryClaims.recentReleasesCount', { count: data.releases.length })}</h2>
          {data.releases.length === 0 ? (
            <div className="dc-empty">{t('deliveryClaims.empty')}</div>
          ) : (
            <div className="dc-table-wrap">
              <table className="dc-table">
                <thead>
                  <tr>
                    <th>{t('escrow.order')}</th>
                    <th>{t('orders.store')}</th>
                    <th>{t('escrow.reason')}</th>
                    <th>{t('deliveryClaims.lastDriver')}</th>
                    <th>{t('deliveryClaims.when')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.releases.map((r, i) => {
                    const rm = reasonMeta(r.reason);
                    return (
                      <tr key={`${r.order_id}-${i}`}>
                        <td><code className="dc-code">{r.order_id}</code></td>
                        <td><code className="dc-code">{(r.store_id || '').slice(0, 12)}…</code></td>
                        <td>
                          <span className="dc-chip" style={{ color: rm.color, background: rm.bg }}>
                            {rm.label}
                          </span>
                        </td>
                        <td>{r.driver_id ? <code className="dc-code">{r.driver_id.slice(0, 12)}…</code> : '—'}</td>
                        <td>{r.updated_at ? new Date(r.updated_at).toLocaleString('en-GB') : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="dc-loading"><div className="mp-spinner" /> Loading…</div>
      )}
    </div>
  );
};

export default DeliveryClaimsPage;
